import React, { useState, useEffect } from "react";
import "./management.css";
import useApi from "../../../shared/hooks/useApi";
import UserManagement from "./UserManagement";
import RoleManagement from "./RoleManagement";
import SettingsManagement from "./SettingsManagement";

const Management = ({ setLoading }) => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [error, setError] = useState(null);
    const [expandedUser, setExpandedUser] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [settings, setSettings] = useState({});
    const { apiGet, apiPost, apiDelete } = useApi();

    useEffect(() => {
        fetchUsers();
        fetchSettings();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await apiGet("UX_HTTP_API", "/get-users");
            console.log("management data", data);
            const rolenames = data.filter(user => user.email === "rolenames");
            const users = data.filter(user => user.email !== "rolenames");
            setRoles(rolenames);
            setUsers(users);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const getDataRoute = `/esid-24-003/fetch_setting_labels`;
            const data = await apiGet("UX_HTTP_API", getDataRoute);
            console.log("settings", data);
            setSettings(data);
        } catch (err) {
            setError(err);
        }
    };

    const updateSetting = async (key, updatedData) => {
        try {
            setLoading(true);
            console.log("updatedData", updatedData);
            console.log("key", key);
            await apiPost("UX_HTTP_API", `/esid-24-003/update_setting_labels`, { key, ...updatedData });
            await fetchSettings();
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const removeSetting = async (key) => {
        try {
            setLoading(true);
            await apiDelete("UX_HTTP_API", `/esid-24-003/remove_setting_labels/${key}`);
            await fetchSettings();
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            console.log("users", users);
            console.log("roles", roles);
            let combinedData = [...users, ...roles];
            setLoading(true);
            setError(null);
            await apiPost('UX_HTTP_API', '/update-users', { users: combinedData });
            setHasChanges(false);
            await fetchUsers();
        } catch (err) {
            console.error('Save failed:', err);
            setError(err.message || 'Failed to save changes');
        } finally {
            setLoading(false);
        }
    };

    const updateUserData = (userEmail, projectIndex, roleKey, field, value) => {
        setUsers(prevUsers => {
            const newUsers = [...prevUsers];
            const userIndex = newUsers.findIndex(u => u.email === userEmail);

            if (userIndex === -1) return prevUsers;

            const newUser = { ...newUsers[userIndex] };
            const project = { ...newUser.projects[projectIndex] };

            if (field === 'newRole') {
                project.roles = {
                    ...project.roles,
                    [roleKey]: { actions: [], pages: [] }
                };
            } else if (field === 'roles') {
                project.roles = value;
            } else {
                const roles = { ...project.roles };
                if (field === 'actions') {
                    roles[roleKey] = {
                        ...roles[roleKey],
                        actions: value
                    };
                } else if (field === 'pages') {
                    roles[roleKey] = {
                        ...roles[roleKey],
                        pages: value
                    };
                }
                project.roles = roles;
            }

            newUser.projects[projectIndex] = project;
            newUsers[userIndex] = newUser;

            setHasChanges(true);
            return newUsers;
        });
    };

    const assignProjectToUser = (userEmail, newProject) => {
        setUsers(prevUsers => {
            const newUsers = [...prevUsers];
            const userIndex = newUsers.findIndex(u => u.email === userEmail);

            if (userIndex === -1) return prevUsers;

            const newUser = { ...newUsers[userIndex] };
            newUser.projects = [...newUser.projects, newProject];
            newUsers[userIndex] = newUser;

            setHasChanges(true);
            return newUsers;
        });
    };

    const updateRolesData = (email, updatedRoles) => {
        setRoles(prevRoles =>
            prevRoles.map(user =>
                user.email === email ? { ...user, roles: updatedRoles } : user
            )
        );
        setHasChanges(true);
    };

    if (error) return <div className="error">Error: {error.message}</div>;

    return (
        <div className="management-page">
            <div className="header">
                <h1>User Role Management</h1>
                {hasChanges && (
                    <button className="save-button" onClick={handleSave}>
                        Save Changes
                    </button>
                )}
            </div>
            <RoleManagement roles={roles} updateRolesData={updateRolesData} />
            <UserManagement 
                users={users} 
                expandedUser={expandedUser} 
                setExpandedUser={setExpandedUser} 
                assignProjectToUser={assignProjectToUser} 
                updateUserData={updateUserData} 
            />
            <SettingsManagement settings={settings} updateSetting={updateSetting} removeSetting={removeSetting} />
        </div>
    );
};

export default Management;